import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check token expiry every minute for 24-hour tokens
    const interval = setInterval(checkTokenExpiry, 60000);
    
    const handleAuthCode = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const authCode = urlParams.get('code');
      
      if (authCode) {
        try {
          // Process auth code from URL
          setLoading(true);
          // Remove code from URL to prevent issues on refresh
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Exchange code for tokens
          const tokenData = await authService.exchangeAuthCode(authCode);
          
          if (tokenData && tokenData.idToken) {
            
            // Immediately check auth state to initialize the user session
            try {
              await checkAuthState();
              
              // Only redirect if we're not already on the home page
              if (window.location.pathname !== '/') {
                window.location.href = '/';
              }
              return;
            } catch (sessionError) {
              console.error('Failed to initialize session after token exchange:', sessionError);
              // Continue with page reload as fallback
              window.location.href = '/';
              return;
            }
          } else {
            console.error('No token data received after exchange');
            setError('Authentication failed. Please try again.');
          }
        } catch (error) {
          console.error('Error handling auth code:', error);
          setError('Failed to complete authentication. Please try again.');
        } finally {
          setLoading(false);
        }
      }
      
      // Normal authentication check if no auth code
      checkAuthState();
    };
    
    handleAuthCode();
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Also check token expiry when user becomes active (focus/click)
  useEffect(() => {
    const handleUserActivity = () => {
      if (user) {
        checkTokenExpiry();
      }
    };

    window.addEventListener('focus', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);

    return () => {
      window.removeEventListener('focus', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
    };
  }, [user]);

  const checkAuthState = async () => {
    try {
      // First try to refresh the token if needed
      try {
        await authService.refreshSession();
        // Session refreshed
      } catch (refreshError) {
        // Don't log the full error for federated logins, as this is expected
        // Refresh not needed or failed - expected for federated logins
      }
      
      // Then check authentication
      const isAuthenticated = await authService.isAuthenticated();
      if (isAuthenticated) {
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          // User authenticated
        } catch (userError) {
          console.error('Error getting current user:', userError);
          
          // If we have tokens but can't get the user, try to extract user info from the ID token
          const idToken = authService.getIdToken();
          if (idToken) {
            try {
              const payload = JSON.parse(atob(idToken.split('.')[1]));
              const userFromToken = {
                username: payload.email || payload['cognito:username'],
                attributes: {
                  email: payload.email,
                  given_name: payload.given_name || '',
                  family_name: payload.family_name || '',
                  name: payload.name || ''
                },
                session: { isValid: () => true }
              };
              setUser(userFromToken);
              // Session created from token
            } catch (tokenError) {
              console.error('Failed to parse ID token:', tokenError);
              throw userError; // Re-throw the original error
            }
          } else {
            throw userError;
          }
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    setLoading(true);
    setError('');
    let mfaRequired = false;
    try {
      const result = await authService.signIn(credentials.email, credentials.password, credentials.mfaCode);
      
      if (result.requiresMFA && !credentials.mfaCode) {
        mfaRequired = true;
        setLoading(false);
        return result;
      }
      
      if (credentials.cognitoUser && credentials.mfaCode) {
        const mfaResult = await authService.confirmMFA(credentials.cognitoUser, credentials.mfaCode);
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
        return mfaResult;
      }
      
      if (result.requiresMFA && credentials.mfaCode) {
        const mfaResult = await authService.confirmMFA(result.cognitoUser, credentials.mfaCode);
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
        return mfaResult;
      }
      
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      return result;
    } catch (error) {
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      if (!mfaRequired) {
        setLoading(false);
      }
    }
  };

  const signup = async (userData) => {
    setLoading(true);
    setError('');
    try {
      if (userData.isVerification) {
        const result = await authService.confirmSignUp(userData.email, userData.verificationCode);
        setLoading(false);
        return result;
      } else {
        await authService.signUp(
          userData.email,
          userData.password,
          userData.firstName,
          userData.lastName
        );
        setLoading(false);
        return { needsVerification: true, email: userData.email };
      }
    } catch (error) {
      setError(error.message || 'Signup failed');
      setLoading(false);
      return { error: error.message || 'Signup failed' };
    }
  };

  const clearError = () => {
    setError('');
  };

  const checkTokenExpiry = () => {
    const idToken = authService.getIdToken();
    if (idToken && user) {
      try {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = payload.exp - currentTime;
        
        // Store the actual token duration for reference
        const tokenDuration = (payload.exp - payload.iat) / 3600;
        const tokenTotalDuration = tokenDuration.toFixed(2);
        if (!localStorage.getItem('tokenTotalDuration')) {
          localStorage.setItem('tokenTotalDuration', tokenTotalDuration);
        }
        
        if (timeUntilExpiry <= 0) {
          setError('Your session has expired. Please sign in again.');
          logout();
          return;
        }
        
        // Get the actual token duration from localStorage or calculate it
        const storedDuration = localStorage.getItem('tokenTotalDuration');
        const actualTokenDuration = storedDuration ? parseFloat(storedDuration) * 3600 : (payload.exp - payload.iat);
        
        // Only refresh if less than 10% of token lifetime remains
        const refreshThreshold = actualTokenDuration * 0.1;
        
        // Try to refresh the token if it's about to expire based on its actual duration
        if (timeUntilExpiry < refreshThreshold) {
          authService.refreshSession()
            .then(() => {
              // Clear any existing warnings
              if (error && (error.toLowerCase().includes('session') || error.toLowerCase().includes('expire'))) {
                clearError();
              }
            })
            .catch(err => {
              // Only show warning when 5% of token lifetime remains
              const warningThreshold = actualTokenDuration * 0.05;
              
              if (timeUntilExpiry <= warningThreshold) {
                const minutesLeft = Math.ceil(timeUntilExpiry / 60);
                const hoursLeft = Math.floor(minutesLeft / 60);
                
                let timeMessage;
                if (hoursLeft > 0) {
                  const remainingMinutes = minutesLeft % 60;
                  timeMessage = `${hoursLeft} hour${hoursLeft > 1 ? 's' : ''}${remainingMinutes > 0 ? ` and ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}` : ''}`;
                } else {
                  timeMessage = `${minutesLeft} minute${minutesLeft > 1 ? 's' : ''}`;
                }
                
                setError(`Your session will expire in ${timeMessage}. Please save your work.`);
              }
            });
        } else {
          // Clear any expiry warnings if we're outside the warning window
          if (error && (error.toLowerCase().includes('session') || error.toLowerCase().includes('expire'))) {
            clearError();
          }
        }
      } catch (error) {
        console.error('Token parsing error:', error);
        logout();
      }
    }
  };

  const logout = () => {
    authService.signOut();
    setUser(null);
  };

  const setupMFA = async () => {
    try {
      return await authService.setupMFA();
    } catch (error) {
      setError(error.message || 'MFA setup failed');
      throw error;
    }
  };

  const verifyMFASetup = async (totpCode) => {
    try {
      const result = await authService.verifyMFASetup(totpCode);
      await authService.enableMFA();
      return result;
    } catch (error) {
      setError(error.message || 'MFA verification failed');
      throw error;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    clearError,
    setupMFA,
    verifyMFASetup,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;